import React, { useEffect, useRef, useState } from "react";
import { setGazeBenchmarkImage } from "../../../services/apiInterviewService";
import {
  FaceLandmarker,
  FilesetResolver,
  DrawingUtils,
} from "@mediapipe/tasks-vision";

export default function CameraCalibrationScreen({ onSuccess }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const faceLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const lastLandmarksRef = useRef(null);

  const capturedImagesRef = useRef([]);
  const goodFramesRef = useRef(0);
  const allGoodRef = useRef(false);

  const GOOD_NEEDED = 25;

  const [checks, setChecks] = useState({});
  const [instruction, setInstruction] = useState("Initializing...");
  const [metrics, setMetrics] = useState({});
  const [capturing, setCapturing] = useState(false);
  const [isCameraBlocked, setIsCameraBlocked] = useState(false);
  const cameraCheckIntervalRef = useRef(null);

  // Camera permission monitor — polls every 1.5 s
  // If denied: stops the video stream and shows the blocked overlay
  // If restored: restarts the stream and resumes calibration
  useEffect(() => {
    const checkCamera = async () => {
      try {
        if (navigator.permissions) {
          const status = await navigator.permissions.query({ name: "camera" });
          if (status.state === "denied") {
            if (!isCameraBlocked) {
              // Stop the active stream so the browser releases the camera
              if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
                videoRef.current.srcObject = null;
              }
              if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
              }
              setIsCameraBlocked(true);
            }
          } else if (status.state === "granted" && isCameraBlocked) {
            // Camera re-granted — restart stream and calibration loop
            setIsCameraBlocked(false);
            restartStream();
          }
        } else {
          // Fallback: try getUserMedia
          try {
            const s = await navigator.mediaDevices.getUserMedia({
              video: true,
            });
            s.getTracks().forEach((t) => t.stop());
            if (isCameraBlocked) {
              setIsCameraBlocked(false);
              restartStream();
            }
          } catch {
            if (!isCameraBlocked) {
              if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
                videoRef.current.srcObject = null;
              }
              if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
              }
              setIsCameraBlocked(true);
            }
          }
        }
      } catch {
        // Permissions API unavailable — ignore
      }
    };

    cameraCheckIntervalRef.current = setInterval(checkCamera, 1500);
    return () => {
      if (cameraCheckIntervalRef.current) {
        clearInterval(cameraCheckIntervalRef.current);
      }
    };
  }, [isCameraBlocked]);

  // Restarts the camera stream and calibration loop after permission is re-granted
  const restartStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      // Reset calibration state
      goodFramesRef.current = 0;
      allGoodRef.current = false;
      capturedImagesRef.current = [];
      lastLandmarksRef.current = null;
      setChecks({});
      setInstruction("Initializing...");
      setMetrics({});
      startPrediction();
    } catch (e) {
      console.error("Failed to restart stream:", e);
    }
  };

  // ✅ SMOOTHING FUNCTION
  const smoothLandmarks = (prev, curr, alpha = 0.7) => {
    if (!prev) return curr;

    return curr.map((p, i) => ({
      x: prev[i].x * alpha + p.x * (1 - alpha),
      y: prev[i].y * alpha + p.y * (1 - alpha),
      z: prev[i].z * alpha + p.z * (1 - alpha),
    }));
  };

  // ✅ DRAW OVERLAY
  const drawOverlay = (lms, width, height) => {
    const ctx = canvasRef.current.getContext("2d");

    let x0 = 1,
      x1 = 0,
      y0 = 1,
      y1 = 0;

    lms.forEach((l) => {
      x0 = Math.min(x0, l.x);
      x1 = Math.max(x1, l.x);
      y0 = Math.min(y0, l.y);
      y1 = Math.max(y1, l.y);
    });

    const pad = 0.03;
    const bx = (x0 - pad) * width;
    const by = (y0 - pad) * height;
    const bw = (x1 - x0 + pad * 2) * width;
    const bh = (y1 - y0 + pad * 2) * height;

    const cx = bx + bw / 2;
    const cy = by + bh / 2;

    const ctx2 = canvasRef.current.getContext("2d");
    ctx2.beginPath();
    ctx2.ellipse(cx, cy, bw * 0.48, bh * 0.77, 0, 0, Math.PI * 2);
    ctx2.strokeStyle = "transparent";
    ctx2.lineWidth = 1;
    ctx2.stroke();
  };

  // ✅ CALIBRATION LOGIC
  const runCalibrationLogic = (lms) => {
    const size = Math.abs(lms[152].y - lms[10].y);
    const facePercent = Math.round(size * 100);

    const chinY = lms[152].y;

    const leftEye = lms[33];
    const rightEye = lms[263];

    const roll =
      Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) *
      (180 / Math.PI);

    const eyeY = (leftEye.y + rightEye.y) / 2;
    const pitch = (0.5 - eyeY) * 100;

    const goodDistance = size > 0.18 && size < 0.7;
    const goodFrame = chinY > 0.6 && chinY < 0.85;
    const goodTilt = Math.abs(roll) < 12;
    const goodPitch = eyeY > 0.3 && eyeY < 0.5;

    setMetrics({
      face: `${facePercent}%`,
      pitch: `${pitch.toFixed(1)}°`,
      framing: goodFrame ? "Good" : "Adjust",
      tilt: `${roll.toFixed(1)}°`,
    });

    const newChecks = {
      face: { ok: true, text: "Face Detected" },
      distance: {
        ok: goodDistance,
        text: goodDistance ? "Good Distance" : "Adjust Distance",
      },
      pitch: {
        ok: goodPitch,
        text: goodPitch ? "Camera Level" : "Adjust Camera Height",
      },
      frame: {
        ok: goodFrame,
        text: goodFrame ? "Good Framing" : "Adjust Framing",
      },
      tilt: {
        ok: goodTilt,
        text: goodTilt ? "Head Level" : `Tilt ${roll.toFixed(1)}°`,
      },
    };

    setChecks(newChecks);

    let action = "";
    if (!goodDistance) action = "Adjust distance";
    else if (!goodPitch) action = "Adjust camera height";
    else if (!goodFrame) action = "Adjust framing";
    else if (!goodTilt) action = "Straighten head";

    if (action) {
      goodFramesRef.current = 0;
      setInstruction(action);
      return;
    }

    goodFramesRef.current = Math.min(goodFramesRef.current + 1, GOOD_NEEDED);
    setInstruction(`Hold Still (${goodFramesRef.current}/${GOOD_NEEDED})`);

    if (
      goodFramesRef.current >= GOOD_NEEDED &&
      !allGoodRef.current &&
      !capturing
    ) {
      allGoodRef.current = true;
      captureImage();
    }
  };

  // ✅ FACE LOOP (FIXED)
  const startPrediction = () => {
    const predict = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const faceLandmarker = faceLandmarkerRef.current;

      if (!video || !canvas || !faceLandmarker) {
        animationFrameRef.current = requestAnimationFrame(predict);
        return;
      }

      const ctx = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const results = faceLandmarker.detectForVideo(video, performance.now());

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✅ HANDLE DETECTION + PERSISTENCE
      if (results.faceLandmarks?.length) {
        const raw = results.faceLandmarks[0];

        lastLandmarksRef.current = smoothLandmarks(
          lastLandmarksRef.current,
          raw,
        );
      } else {
        setInstruction("Face not detected");
        goodFramesRef.current = 0;
      }

      const lms = lastLandmarksRef.current;

      if (lms) {
        drawOverlay(lms, canvas.width, canvas.height);

        const drawingUtils = new DrawingUtils(ctx);

        drawingUtils.drawConnectors(
          lms,
          FaceLandmarker.FACE_LANDMARKS_TESSELATION,
          { color: "rgba(0,255,0,0.6)", lineWidth: 0.7 },
        );

        drawingUtils.drawConnectors(
          lms,
          FaceLandmarker.FACE_LANDMARKS_FACE_OVAL,
          { color: "rgba(0,255,0,0.6)", lineWidth: 0.7 },
        );

        runCalibrationLogic(lms);
      }

      animationFrameRef.current = requestAnimationFrame(predict);
    };

    predict();
  };

  const initializingRef = useRef(false);

  useEffect(() => {
    let stream;
    let cancelled = false;

    const init = async () => {
      if (initializingRef.current) return; // block concurrent WASM init
      initializingRef.current = true;

      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm",
        );
        if (cancelled) return;

        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          vision,
          {
            baseOptions: {
              modelAssetPath:
                "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
            },
            runningMode: "VIDEO",
            numFaces: 1,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false,
          },
        );
        if (cancelled) return;

        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: "user",
          },
        });

        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (cancelled) return;

        startPrediction();
      } finally {
        initializingRef.current = false;
      }
    };

    init();

    return () => {
      cancelled = true;
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // ✅ IMAGE CAPTURE (unchanged)
  const captureImage = async () => {
    if (capturedImagesRef.current.length >= 3) return;

    const video = videoRef.current;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    canvas.getContext("2d").drawImage(video, 0, 0);

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg"));

    capturedImagesRef.current.push(blob);

    const count = capturedImagesRef.current.length;

    setInstruction(`Captured ${count}/3`);
    setCapturing(true);

    setTimeout(() => {
      setCapturing(false);
      goodFramesRef.current = 0;
      allGoodRef.current = false;
    }, 1000);

    if (count === 3) {
      try {
        await setGazeBenchmarkImage(capturedImagesRef.current);
        onSuccess();
      } catch (error) {
        console.error("Failed to upload gaze benchmark images:", error);
        setInstruction("Upload failed — retrying...");
        // Reset capture state so the calibration loop re-triggers
        // captureImage() once GOOD_NEEDED good frames are held again.
        capturedImagesRef.current = [];
        goodFramesRef.current = 0;
        allGoodRef.current = false;
        setCapturing(false);
      }
    }
  };

  return (
    <div className="w-screen h-screen bg-black relative text-white">
      {/* Camera blocked overlay — same style as Interview.js OfflineScreen */}
      {isCameraBlocked && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(10,13,26,0.97)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <div
            style={{
              background: "#111729",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 20,
              padding: "40px 36px",
              maxWidth: 520,
              width: "100%",
              textAlign: "center",
            }}
          >
            {/* Icon */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  style={{ width: 26, height: 26, color: "#F87171" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.8}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10l4.553-2.277A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 3l18 18"
                  />
                </svg>
              </div>
            </div>

            {/* Heading */}
            <h2
              style={{
                margin: "0 0 10px",
                fontWeight: 700,
                color: "#fff",
                fontSize: 20,
              }}
            >
              Camera access blocked
            </h2>

            {/* Body */}
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 13,
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.6,
              }}
            >
              Camera calibration has been paused.
            </p>
            <p
              style={{
                margin: "0 0 28px",
                fontSize: 14,
                color: "rgba(255,255,255,0.8)",
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Please enable camera permission in your browser settings, then
              return to this tab. Calibration will resume automatically once
              access is restored.
            </p>

            {/* Pulse */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                fontSize: 11,
                color: "rgba(255,255,255,0.3)",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#EF4444",
                  display: "inline-block",
                  animation: "pulse-cal 1.5s ease-in-out infinite",
                }}
              />
              <style>{`@keyframes pulse-cal{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
              Waiting for camera permission…
            </div>
          </div>
        </div>
      )}
      <div className="absolute top-0 w-full bg-black/80 p-3 text-center z-50">
        <div className="text-sm font-semibold">Calibrating Camera...</div>

        <div className="flex flex-wrap justify-center gap-3 mt-2 text-xs">
          {Object.entries(checks).map(([key, val]) => (
            <div
              key={key}
              className={`flex items-center gap-1 ${
                val.ok ? "text-green-400" : "text-yellow-400"
              }`}
            >
              <span>{val.ok ? "✔" : "⚠"}</span>
              <span>{val.text}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 bg-yellow-600/20 text-yellow-300 p-2 rounded">
          {instruction}
        </div>
      </div>

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover scale-x-[-1]"
      />

      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none scale-x-[-1]"
      />

      <div className="absolute bottom-5 w-full flex justify-center gap-6 text-xs">
        <div>Face: {metrics.face}</div>
        <div>Pitch: {metrics.pitch}</div>
        <div>Framing: {metrics.framing}</div>
        <div>Tilt: {metrics.tilt}</div>
      </div>
    </div>
  );
}
